import { useEffect, useState } from 'react'
import Modal from '../../../shared/ui/overlay/Modal/Modal'
import { Button } from '../../../shared/ui/controls/Button/Button'
import { getAbsenceCategoryLabel } from '../../../entities/absence'
import { getReschedulingDetail } from '../../../entities/schedule'
import type { StartGateController } from '../model/useStartGate'

const NOTE_MAX = 200

interface Props {
  gate: StartGateController
  /** What is being started — the noun both sentences are built around. */
  noun: 'Termin' | 'Aufgabe'
}

/**
 * The two dialogs `useStartGate` opens. Styled like `DiscardChangesDialog`, the app's other
 * "really?" prompt, so a worker meets the same shape every time something needs a second tap.
 */
export const StartGateDialogs = ({ gate, noun }: Props) => {
  const { open } = gate
  const [note, setNote] = useState('')
  const article = noun === 'Termin' ? 'diesen Termin' : 'diese Aufgabe'

  // A fresh reason every time the dialog opens; the last one belonged to a different decision.
  const confirmOpen = open?.kind === 'confirm'
  useEffect(() => {
    if (confirmOpen) setNote('')
  }, [confirmOpen])

  const detail = open?.kind === 'confirm' ? getReschedulingDetail(open.cause, open.reason, false) : null

  return (
    <>
      <Modal
        empty
        show={open?.kind === 'confirm'}
        setShow={(next) => {
          if (!next) gate.close()
        }}
        ariaLabel={`${noun} wird umgeplant`}
        className="flex w-full max-w-[520px] flex-col gap-4 p-6"
      >
        <p className="text-base font-semibold text-gray-900">{noun} wird umgeplant</p>

        <p className="text-sm text-gray-600">
          Das Büro plant {article} gerade um
          {detail ? <> (Grund: {detail})</> : null}. Trotzdem jetzt durchführen? Das Büro wird
          benachrichtigt.
        </p>

        <label className="flex flex-col gap-1 text-sm text-gray-700">
          Grund (optional)
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
            maxLength={NOTE_MAX}
            rows={2}
            placeholder="z. B. Mieter war da"
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </label>

        {/* Safe action first in the DOM, as in `DiscardChangesDialog`. */}
        <div className="flex flex-row-reverse gap-3">
          <Button
            onClick={gate.close}
            className="flex-1 rounded-[30px] bg-[#716D6B] font-bold text-white h-[46px]"
            type="button"
          >
            Abbrechen
          </Button>
          <Button
            onClick={() => gate.confirm(note)}
            className="flex-1 rounded-[30px] bg-amber-600 font-bold text-white h-[46px]"
            type="button"
          >
            Trotzdem starten
          </Button>
        </div>
      </Modal>

      <Modal
        empty
        show={open?.kind === 'blocked'}
        setShow={(next) => {
          if (!next) gate.close()
        }}
        ariaLabel={`${noun} kann nicht gestartet werden`}
        className="flex w-full max-w-[520px] flex-col gap-4 p-6"
      >
        <p className="text-base font-semibold text-gray-900">
          {noun} kann nicht gestartet werden
        </p>
        <p className="text-sm text-gray-600">
          Sie sind zu dieser Zeit als{' '}
          {open?.kind === 'blocked' ? getAbsenceCategoryLabel(open.reason) : 'abwesend'} eingetragen.
          Bitte melden Sie sich im Büro.
        </p>
        <Button
          onClick={gate.close}
          className="rounded-[30px] bg-[#716D6B] font-bold text-white h-[46px]"
          type="button"
        >
          OK
        </Button>
      </Modal>
    </>
  )
}
